import { create } from 'zustand'
import { serverApi, channelApi } from '../../services/api'
import { getRequestActiveServer, setRequestActiveServer } from '../../services/activeServer'
import type { Server, Channel } from '../../types'

interface ServerState {
  servers: Server[]
  // Flips to true after the first successful (or failing) fetchServers
  // resolves. Lets consumers distinguish "still loading" from "loaded
  // and the requested id genuinely doesn't exist". Without this, a
  // stale serverId in the URL (e.g. browser-restored after a dev DB
  // wipe) shows "Loading server..." forever instead of redirecting
  // home.
  serversLoaded: boolean
  activeServerId: string | null
  setActiveServer: (id: string | null) => void
  fetchServers: () => Promise<void>
  createServer: (data: { name: string; iconUrl?: string }) => Promise<Server>
  updateServer: (id: string, data: Partial<Server>) => Promise<Server>
  deleteServer: (id: string) => Promise<void>
  addServer: (server: Server) => void
  // Client-First: synthesize a temp channel under the right server +
  // category so the sidebar updates instantly. The real API call runs
  // in the background; replaceTempChannel swaps for the canonical row
  // when it lands. Errors drop the placeholder and re-throw.
  createChannelOptimistic: (
    serverId: string,
    // OSPROD-131: extends with optional `topic` (channel description) and
    // `memberUserIds` (members picked at create time). Non-empty members
    // → server marks channel private.
    data: {
      name: string
      type: 'TEXT' | 'VOICE'
      categoryId?: string
      topic?: string
      memberUserIds?: string[]
    }
  ) => { tempId: string; promise: Promise<Channel> }
  replaceTempChannel: (serverId: string, tempId: string, real: Channel) => void
  removeTempChannel: (serverId: string, tempId: string) => void
  // Flip a channel's lock badge in the sidebar without a fetchServers
  // round-trip. Driven by the `voice:channel:locked` socket event so
  // every connected client sees the badge appear/disappear the moment
  // the host toggles the room.
  setChannelLocked: (channelId: string, isLocked: boolean) => void
  // Per-server feature subscriptions. Patched in-place from the
  // `server:modules:updated` socket event (admin saved a change in
  // /admin → Server modules drawer). PrimarySidebar re-runs its
  // rail filter on the next render.
  applyModulesUpdate: (serverId: string, enabledModules: Record<string, boolean>) => void
}

export const useServerStore = create<ServerState>((set, get) => ({
  servers: [],
  serversLoaded: false,
  activeServerId: null,

  setActiveServer: (id) => {
    // Mirror non-null selections into the X-Server-Id header source. Null is
    // NOT mirrored: route changes clear activeServerId when leaving
    // /servers/:id, but the last chosen server should keep scoping requests
    // app-wide and be restored from localStorage after a reload. (Distinct
    // from user.settings.lastActiveServerId, which drives the useActiveServer
    // UI fallback chain.)
    if (id) setRequestActiveServer(id)
    set({ activeServerId: id })
  },

  fetchServers: async () => {
    try {
      const res = await serverApi.list()
      set({ servers: res.data, serversLoaded: true })
    } catch (err) {
      // Mark loaded even on failure so consumers don't hang on the
      // "Loading…" placeholder forever; servers list stays empty.
      set({ serversLoaded: true })
      throw err
    }
  },

  createServer: async (data) => {
    const res = await serverApi.create(data)
    set({ servers: [...get().servers, res.data] })
    return res.data
  },

  updateServer: async (id, data) => {
    const res = await serverApi.update(id, data)
    set({
      servers: get().servers.map((s) => (s.id === id ? res.data : s)),
    })
    return res.data
  },

  deleteServer: async (id) => {
    await serverApi.delete(id)
    // Clear the X-Server-Id header source if it points at the deleted server —
    // whether that's the in-route active server OR a lingering last-selection
    // (incl. one restored from localStorage on reload) — so requests stop
    // carrying a now-deleted server id.
    if (get().activeServerId === id || getRequestActiveServer() === id) setRequestActiveServer(null)
    set({ servers: get().servers.filter((s) => s.id !== id), activeServerId: null })
  },

  addServer: (server) => {
    set({ servers: [...get().servers, server] })
  },

  createChannelOptimistic: (serverId, data) => {
    const tempId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `temp-ch-${crypto.randomUUID()}`
        : `temp-ch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    const placeholder: Channel = {
      id: tempId,
      name: data.name,
      type: data.type,
      position: 9999, // bottom of the list — server returns the real position
      categoryId: data.categoryId ?? null,
      serverId,
      createdAt: new Date().toISOString(),
    }
    // Inject the placeholder into the right slot:
    // - If a categoryId is set, append to that category's channels
    // - Otherwise append to the server's flat channels list (legacy
    //   uncategorized channels still live there)
    set({
      servers: get().servers.map((s) => {
        if (s.id !== serverId) return s
        if (data.categoryId) {
          return {
            ...s,
            categories: s.categories.map((cat) =>
              cat.id === data.categoryId
                ? { ...cat, channels: [...cat.channels, placeholder] }
                : cat
            ),
          }
        }
        return { ...s, channels: [...(s.channels || []), placeholder] }
      }),
    })

    const promise = channelApi
      .create(serverId, data)
      .then((res) => {
        useServerStore.getState().replaceTempChannel(serverId, tempId, res.data)
        return res.data as Channel
      })
      .catch((err) => {
        useServerStore.getState().removeTempChannel(serverId, tempId)
        throw err
      })

    return { tempId, promise }
  },

  replaceTempChannel: (serverId, tempId, real) => {
    set({
      servers: get().servers.map((s) => {
        if (s.id !== serverId) return s
        return {
          ...s,
          channels: (s.channels || []).map((c) => (c.id === tempId ? real : c)),
          categories: s.categories.map((cat) => ({
            ...cat,
            channels: cat.channels.map((c) => (c.id === tempId ? real : c)),
          })),
        }
      }),
    })
  },

  removeTempChannel: (serverId, tempId) => {
    set({
      servers: get().servers.map((s) => {
        if (s.id !== serverId) return s
        return {
          ...s,
          channels: (s.channels || []).filter((c) => c.id !== tempId),
          categories: s.categories.map((cat) => ({
            ...cat,
            channels: cat.channels.filter((c) => c.id !== tempId),
          })),
        }
      }),
    })
  },

  setChannelLocked: (channelId, isLocked) => {
    set({
      servers: get().servers.map((s) => ({
        ...s,
        channels: (s.channels || []).map((c) =>
          c.id === channelId ? { ...c, isLocked } : c
        ),
        categories: (s.categories || []).map((cat) => ({
          ...cat,
          channels: (cat.channels || []).map((c) =>
            c.id === channelId ? { ...c, isLocked } : c
          ),
        })),
      })),
    })
  },

  applyModulesUpdate: (serverId, enabledModules) => {
    set({
      servers: get().servers.map((s) =>
        s.id === serverId ? { ...s, enabledModules } : s,
      ),
    })
  },
}))
